import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

import { StorageHelper } from "../../utils/storageHelper";
import { DEFAULT_MAILBOX, isMailboxKey } from "../../utils/types/email";
import {
  fetchEmails,
  fetchEmailById,
  toggleEmailFlag,
} from "../thunks/emailThunks";

import type { UIMessage } from "../../hooks/useAiAssistant";
import type { EmailListItem, EmailDetails, MashedEmail, MailboxKey, EmailViewMode } from "../../utils/types/email";
import type { RootState } from "../store";

export { DEFAULT_MAILBOX, isMailboxKey } from "../../utils/types/email";

export const DRAFT_TTL_MS = 30 * 24 * 60 * 60 * 1000;

// Unified draft object per email, replacing the legacy drafts and draftSubjects dual-map structure
export interface EmailDraft {
  body?: string;
  // Distinguishes an intentionally cleared new-message body from a composer that
  // has never been edited. This lets an auto-added signature remain an initial
  // baseline without losing a user's decision to remove it.
  bodyModified?: boolean;
  subject?: string;
  replyTo?: any;
  replyCc?: any;
  replyBcc?: any;
  composeTo?: any; // Added follow-up: primary recipients were missing from draft save (only composeCc/composeBcc were included in original ticket scope)
  composeCc?: any;
  composeBcc?: any;
  selectedAttachments?: any;
  moveToFolder?: any;
  replyMode?: any;
  lastUpdated?: number;
  // Persisted job IDs for vessel/voyage context in new-compose drafts.
  // Only job_id values are stored (not full VesselJob objects) because:
  // 1. No single-job API exists to refetch individual jobs
  // 2. Jobs are always refetched via the bulk jobListStatic endpoint
  // 3. Storing full objects would duplicate large payloads and risk stale/inconsistent data
  vesselJobIds?: string[];
}

// Note: "emailId" throughout this slice refers to the unique identifier of an email/message
// (e.g. a Microsoft Graph message ID).
export interface PersistedDraftEntry extends EmailDraft {
  emailId: string;
  mailbox: string;
  lastUpdated: number;
  // Explicit type field added for clarity — "new" for compose-from-scratch, "reply" for reply drafts.
  // Redundant with emailId prefix convention ("new-compose-" prefix) but both are kept for
  // backward compatibility: newer code can check draftType directly; older persisted drafts
  // that lack this field fall back to the prefix check during restoration.
  draftType?: "new" | "reply";
}

// Checks every field (not just body/subject) since EmailDraft now holds recipients, attachments, and reply mode
export function isDraftEmpty(draft: EmailDraft | null | undefined): boolean {
  if (!draft) return true;
  const hasBody = typeof draft.body === "string" && draft.body.trim().length > 0;
  const hasBodyModification = draft.bodyModified === true;
  const hasSubject = typeof draft.subject === "string" && draft.subject.trim().length > 0;
  const hasReplyTo = Array.isArray(draft.replyTo);
  const hasReplyCc = Array.isArray(draft.replyCc);
  const hasReplyBcc = Array.isArray(draft.replyBcc);
  const hasComposeTo = Array.isArray(draft.composeTo) && draft.composeTo.length > 0;
  const hasComposeCc = Array.isArray(draft.composeCc) && draft.composeCc.length > 0;
  const hasComposeBcc = Array.isArray(draft.composeBcc) && draft.composeBcc.length > 0;
  const hasAttachments = Array.isArray(draft.selectedAttachments) && draft.selectedAttachments.length > 0;
  return !(
    hasBody ||
    hasBodyModification ||
    hasSubject ||
    hasReplyTo ||
    hasReplyCc ||
    hasReplyBcc ||
    hasComposeTo ||
    hasComposeCc ||
    hasComposeBcc ||
    hasAttachments
  );
}

// Never allows empty or stale incoming data to overwrite a valid local draft; ties broken by lastUpdated timestamp
export function resolveDraft<T extends { lastUpdated?: number } & EmailDraft>(
  local: T | null | undefined,
  incoming: T | null | undefined
): T | null | undefined {
  if (!local) return incoming ?? null;
  if (!incoming) return local;
  if (isDraftEmpty(incoming)) return local;
  const localTime = typeof local.lastUpdated === "number" ? local.lastUpdated : 0;
  const incomingTime = typeof incoming.lastUpdated === "number" ? incoming.lastUpdated : 0;
  if (localTime > incomingTime) return local;
  return incoming;
}

// Key format includes user identifier and mailbox to prevent cross-user draft leakage and scope drafts per mailbox
export const getDraftStorageKey = (
  userIdentifier: string | null | undefined,
  mailbox: string,
  emailId: string
): string | null => {
  if (!userIdentifier) return null;
  return `email_${userIdentifier}_${mailbox}_draft_${emailId}`;
};

// Persists non-empty draft data to local storage with timestamp, or clears key if empty
export const saveDraftToStorage = (
  storageKey: string | null | undefined,
  draftData: { emailId: string; mailbox: string } & EmailDraft
): void => {
  if (!storageKey) return;
  if (!isDraftEmpty(draftData)) {
    try {
      // Infer draft type from emailId prefix and save explicitly alongside existing fields.
      // Both the prefix convention and this field are kept for backward compatibility —
      // the prefix remains the legacy mechanism; draftType is the new canonical indicator.
      const draftType: "new" | "reply" = draftData.emailId.startsWith("new-compose-")
        ? "new"
        : "reply";
      StorageHelper.setLocal(storageKey, {
        ...draftData,
        draftType,
        lastUpdated: Date.now(),
      });
    } catch (error) {
      console.warn("Failed to save draft to local storage:", error);
    }
  } else {
    clearDraftFromStorage(storageKey);
  }
};

// Safely removes a draft key from localStorage
export const clearDraftFromStorage = (storageKey: string | null | undefined): void => {
  if (!storageKey) return;
  try {
    localStorage.removeItem(storageKey);
  } catch (error) {
    console.warn("Failed to clear draft from local storage:", error);
  }
};

// Scans localStorage for active user drafts and automatically purges entries older than 30 days
export const getAllStoredDrafts = (
  userIdentifier: string | null | undefined
): PersistedDraftEntry[] => {
  if (!userIdentifier) return [];
  const prefix = `email_${userIdentifier}_`;
  const drafts: PersistedDraftEntry[] = [];
  const now = Date.now();
  try {
    const keys = Object.keys(localStorage);
    for (const key of keys) {
      if (key.startsWith(prefix) && key.includes("_draft_")) {
        try {
          const entry = StorageHelper.getLocal(key) as PersistedDraftEntry | null;
          if (entry && typeof entry === "object" && entry !== null && entry.emailId) {
            if (typeof entry.lastUpdated === "number" && now - entry.lastUpdated > DRAFT_TTL_MS) {
              clearDraftFromStorage(key);
            } else {
              drafts.push(entry);
            }
          }
        } catch (err) {
          console.warn(`Failed to read draft for key ${key}:`, err);
        }
      }
    }
  } catch (err) {
    console.warn("Failed to read drafts from local storage:", err);
  }
  return drafts;
};

// Re-hydrates Redux drafts map from localStorage for a specific user and mailbox (or all mailboxes if omitted)
export const rehydrateUserDrafts = (
  userIdentifier: string | null | undefined,
  targetMailbox?: string | null
) => (dispatch: any) => {
  if (!userIdentifier) return;
  try {
    const drafts = getAllStoredDrafts(userIdentifier);
    for (const draft of drafts) {
      if (!targetMailbox || draft.mailbox === targetMailbox) {
        const resolved = resolveDraft(draft, undefined);
        if (resolved) {
          dispatch(setEmailDraft(resolved));
        }
      }
    }
  } catch (error) {
    console.warn("Failed to rehydrate email drafts:", error);
  }
};


// Clears all persisted local storage drafts for a specific user upon sign out
export const clearAllDraftsForUser = (
  userIdentifier: string | null | undefined
): void => {
  if (!userIdentifier) return;
  const prefix = `email_${userIdentifier}_`;
  try {
    const keys = Object.keys(localStorage);
    for (const key of keys) {
      if (key.startsWith(prefix) && key.includes("_draft_")) {
        clearDraftFromStorage(key);
      }
    }
  } catch (err) {
    console.warn("Failed to clear user drafts from local storage:", err);
  }
};

/**
 * Shared helper to reopen a new-compose draft tab from a persisted draft entry.
 * Used by both initial mount restoration and sidebar click handler for closed drafts.
 */
export const reopenDraftTab = (
  draft: { emailId: string; mailbox: string; subject?: string },
  dispatch: any
): boolean => {
  // Validate mailbox
  if (!isMailboxKey(draft.mailbox)) {
    console.warn(
      `Cannot reopen draft tab for ${draft.emailId}: invalid mailbox`,
      draft
    );
    return false;
  }

  // Rebuild tab title from draft's subject, defaulting to "(no subject)"
  const title = typeof draft.subject === "string" && draft.subject.trim()
    ? draft.subject.trim()
    : "(no subject)";

  // Reopen the compose tab
  dispatch(
    openNewComposeTab({
      id: draft.emailId,
      mailbox: draft.mailbox,
      title,
    })
  );
  return true;
};

/**
 * Shared helper to discard an email draft.
 * Consolidates the core discard sequence (Redux draft state removal, localStorage deletion,
 * and closing the draft tab if open) into one place, replacing logic previously duplicated
 * across EmailComposer, EmailDetails, and EmailClient.
 */
export const discardDraft = (
  draft: {
    emailId: string;
    userKey?: string | null;
    mailbox: string;
    openEmailTabs?: OpenEmailTab[];
  },
  dispatch: any
): void => {
  const { emailId, userKey, mailbox, openEmailTabs } = draft;

  // 1. Remove draft from Redux state
  dispatch(clearEmailDraft(emailId));

  // 2. Clear draft from local storage
  const storageKey = getDraftStorageKey(userKey, mailbox, emailId);
  if (storageKey) {
    clearDraftFromStorage(storageKey);
  }

  // 3. If a tab with this id is currently open, close it
  if (openEmailTabs && openEmailTabs.some((tab) => tab.id === emailId)) {
    dispatch(closeEmailTab(emailId));
  }
};

export interface AiReviewData {
  content: string;
  suggestions: string[];
  hasGenerated: boolean;
  timestamp: number;
}

export interface AiChatData {
  messages: UIMessage[];
  lastUpdated: number;
}

export type SortOrder = 'receivedDateTime desc' | 'receivedDateTime asc' | 'from/emailAddress/name asc' | 'from/emailAddress/name desc';

export interface OpenEmailTab {
  id: string;
  emailId: string;
  mailbox: MailboxKey;
  title: string;
  mashedEmail: MashedEmail | null;
  pinned: boolean;
  // Marks a tab as a new-message draft (not backed by a real server email) so it can be
  // rendered as a composer instead of EmailDetails and excluded from fetchEmailById calls.
  isNewCompose?: boolean;
}

export type ExtractionOpType = "task" | "cargo" | "portcall" | "contact";

export interface AiExtractionsData {
  task?: string[];
  cargo?: string[];
  portcall?: string[];
  contact?: string[];
}

export interface EmailState {
  emails: EmailListItem[];
  selectedEmailId: string | null;
  selectedEmailDetails: EmailDetails | null;
  currentMashedEmail: MashedEmail | null;
  openEmailTabs: OpenEmailTab[];
  activeEmailTabId: string | null;
  previewEmailTabId: string | null;
  emailDetailsById: Record<string, EmailDetails>;
  detailsLoadingById: Record<string, boolean>;
  currentFolder: string;
  currentMailbox: MailboxKey;
  listLoading: boolean;
  detailsLoading: boolean;
  error: string | null;
  unreadCount: number;
  aiReviews: Record<string, AiReviewData>;
  aiChats: Record<string, AiChatData>;
  aiExtractions: Record<string, AiExtractionsData>;
  drafts: Record<string, EmailDraft>;
  // Tracks open reply composers in Redux because EmailDetails unmounts per inactive tab
  openReplies: Record<string, boolean>;
  // Persistent UI state — survives tab/screen navigation
  sortOrder: SortOrder;
  activeFilter: string | null;
  myVoyagesEnabled: boolean;
  viewMode: EmailViewMode;
  removingVesselId: string | null;
}

const initialState: EmailState = {
  emails: [],
  selectedEmailId: null,
  selectedEmailDetails: null,
  currentMashedEmail:null,
  openEmailTabs: [],
  activeEmailTabId: null,
  previewEmailTabId: null,
  emailDetailsById: {},
  detailsLoadingById: {},
  currentFolder: "", // Empty means "use backend default Inbox" until a real folder is chosen
  currentMailbox: DEFAULT_MAILBOX,
  listLoading: false,
  detailsLoading: false,
  error: null,
  unreadCount: 0,
  aiReviews: {},
  aiChats: {},
  aiExtractions: {},
  drafts: {},
  openReplies: {},
  sortOrder: 'receivedDateTime desc',
  activeFilter: null,
  myVoyagesEnabled: false,
  viewMode: "flat",
  removingVesselId: null,
};

const getEmailTabId = (mailbox: MailboxKey, emailId: string) =>
  `${mailbox}:${emailId}`;

const getEmailIdFromFetchArg = (
  arg: string | { id: string; mailbox?: MailboxKey },
) => (typeof arg === "string" ? arg : arg.id);

const syncSelectedEmailFromActiveTab = (state: EmailState) => {
  const activeTab = state.openEmailTabs.find(
    (tab) => tab.id === state.activeEmailTabId,
  );

  state.selectedEmailId = activeTab?.emailId ?? null;
  state.currentMashedEmail = activeTab?.mashedEmail ?? null;
  state.selectedEmailDetails = activeTab
    ? state.emailDetailsById[activeTab.emailId] ?? null
    : null;
};

const emailSlice = createSlice({
  name: "emails",
  initialState,
  reducers: {
    setViewMode: (state, action: PayloadAction<EmailViewMode>) => {
      state.viewMode = action.payload;
    },
    selectEmail: (state, action: PayloadAction<{
      emailId: string | null;
      mashedEmail:MashedEmail|null;
    }|null>) => {
      state.selectedEmailId = action.payload?.emailId||null;
      state.currentMashedEmail=action.payload?.mashedEmail||null;
      if (!action.payload) {
        state.selectedEmailDetails = null;
        state.activeEmailTabId = null;
      }
    },
    openEmailPreviewTab: (
      state,
      action: PayloadAction<{
        emailId: string;
        mailbox: MailboxKey;
        title: string;
        mashedEmail: MashedEmail | null;
      }>,
    ) => {
      const { emailId, mailbox, title, mashedEmail } = action.payload;
      const tabId = getEmailTabId(mailbox, emailId);
      const existingTab = state.openEmailTabs.find((tab) => tab.id === tabId);

      if (existingTab) {
        existingTab.title = title;
        existingTab.mashedEmail = mashedEmail;
        state.activeEmailTabId = existingTab.id;
        syncSelectedEmailFromActiveTab(state);
        return;
      }

      const previewIndex = state.previewEmailTabId
        ? state.openEmailTabs.findIndex(
            (tab) => tab.id === state.previewEmailTabId && !tab.pinned,
          )
        : -1;
      const previewTab: OpenEmailTab = {
        id: tabId,
        emailId,
        mailbox,
        title,
        mashedEmail,
        pinned: false,
      };

      if (previewIndex >= 0) {
        state.openEmailTabs[previewIndex] = previewTab;
      } else {
        state.openEmailTabs.push(previewTab);
      }

      state.previewEmailTabId = tabId;
      state.activeEmailTabId = tabId;
      syncSelectedEmailFromActiveTab(state);
    },
    // Opens a new-message draft as its own tab, alongside any already-open email tabs.
    // The tab's id doubles as its "emailId" so it plugs directly into the existing
    // drafts/localStorage/AI-state maps that are already keyed by emailId.
    openNewComposeTab: (
      state,
      action: PayloadAction<{ id: string; mailbox: MailboxKey; title?: string }>,
    ) => {
      const { id, mailbox, title } = action.payload;
      const newTab: OpenEmailTab = {
        id,
        emailId: id,
        mailbox,
        title: title || "New Message",
        mashedEmail: null,
        pinned: true,
        isNewCompose: true,
      };
      state.openEmailTabs.push(newTab);
      state.activeEmailTabId = id;
      syncSelectedEmailFromActiveTab(state);
    },
    updateOpenEmailTabTitle: (
      state,
      action: PayloadAction<{ id: string; title: string }>,
    ) => {
      const tab = state.openEmailTabs.find((item) => item.id === action.payload.id);
      if (!tab) return;
      tab.title = action.payload.title || tab.title;
    },
    setActiveEmailTab: (state, action: PayloadAction<string>) => {
      if (!state.openEmailTabs.some((tab) => tab.id === action.payload)) return;
      state.activeEmailTabId = action.payload;
      syncSelectedEmailFromActiveTab(state);
    },
    pinEmailTab: (state, action: PayloadAction<string>) => {
      const tab = state.openEmailTabs.find((item) => item.id === action.payload);
      if (!tab) return;

      tab.pinned = true;
      if (state.previewEmailTabId === tab.id) {
        state.previewEmailTabId = null;
      }
    },
    closeEmailTab: (state, action: PayloadAction<string>) => {
      const closingIndex = state.openEmailTabs.findIndex(
        (tab) => tab.id === action.payload,
      );
      if (closingIndex === -1) return;

      // Removes open reply tracking state when tab is closed to prevent state leak across tab sessions
      const closingTab = state.openEmailTabs[closingIndex];
      if (closingTab?.emailId) {
        delete state.openReplies[closingTab.emailId];
      }

      const wasActive = state.activeEmailTabId === action.payload;
      const wasPreview = state.previewEmailTabId === action.payload;
      state.openEmailTabs.splice(closingIndex, 1);
      if (wasPreview) {
        state.previewEmailTabId = null;
      }

      if (wasActive) {
        const nextIndex = Math.min(closingIndex, state.openEmailTabs.length - 1);
        state.activeEmailTabId =
          nextIndex >= 0 ? state.openEmailTabs[nextIndex].id : null;
      }

      syncSelectedEmailFromActiveTab(state);
    },
    updateOpenEmailTabMashedEmail: (
      state,
      action: PayloadAction<{
        emailId: string;
        mailbox?: MailboxKey;
        mashedEmail: MashedEmail;
      }>,
    ) => {
      const tab = state.openEmailTabs.find(
        (item) =>
          item.emailId === action.payload.emailId &&
          (!action.payload.mailbox || item.mailbox === action.payload.mailbox),
      );
      if (!tab) return;

      tab.mashedEmail = action.payload.mashedEmail;
      tab.title = action.payload.mashedEmail.email.subject || tab.title;
      if (state.activeEmailTabId === tab.id) {
        syncSelectedEmailFromActiveTab(state);
      }
    },
    changeFolder: (state, action: PayloadAction<string>) => {
      state.currentFolder = action.payload;
    },

    changeMailbox: (state, action: PayloadAction<MailboxKey>) => {
      if (state.currentMailbox === action.payload) return;
      state.currentMailbox = action.payload;
      // Reset all per-mailbox in-memory Redux state when switching mailboxes.
      // Note on keying architecture: Although Microsoft Graph API guarantees message IDs are unique
      // only per-mailbox, Graph IDs are ~150+ char base64 strings (effectively unique across mailboxes in practice)
      // and the UI architecture only ever displays one active mailbox's emails/tabs at a time.
      // Therefore, full composite keying (mailbox:emailId) across all Redux maps was not required.
      // Clearing drafts, aiChats, and aiReviews here prevents stale cross-mailbox state in Redux.
      // (Persisted drafts in localStorage are mailbox-scoped via getDraftStorageKey and remain safely intact).
      state.currentFolder = "";
      state.selectedEmailId = null;
      state.selectedEmailDetails = null;
      state.currentMashedEmail = null;
      state.openEmailTabs = [];
      state.activeEmailTabId = null;
      state.previewEmailTabId = null;
      state.emails = [];
      state.activeFilter = null;
      state.myVoyagesEnabled = false;
      state.openReplies = {};
      state.drafts = {};
      state.aiChats = {};
      state.aiReviews = {};
      state.aiExtractions = {};
    },

    resetSelectedEmail: (state) => {  
      state.selectedEmailId = null;
      state.selectedEmailDetails = null;
      state.currentMashedEmail=null;
      state.activeEmailTabId = null;
    },

    setAiReview: (
      state,
      action: PayloadAction<{
        emailId: string;
        content: string;
        suggestions: string[];
      }>
    ) => {
      const { emailId, content, suggestions } = action.payload;
      state.aiReviews[emailId] = {
        content,
        suggestions,
        hasGenerated: true,
        timestamp: Date.now(),
      };
    },

    setAiExtractions: (
      state,
      action: PayloadAction<{
        emailId: string;
        opType: ExtractionOpType;
        proposals: string[];
      }>
    ) => {
      const { emailId, opType, proposals } = action.payload;
      if (!state.aiExtractions[emailId]) {
        state.aiExtractions[emailId] = {};
      }
      state.aiExtractions[emailId][opType] = proposals;
    },

    dismissAiExtractionItem: (
      state,
      action: PayloadAction<{
        emailId: string;
        opType: ExtractionOpType;
        index: number;
      }>
    ) => {
      const { emailId, opType, index } = action.payload;
      if (state.aiExtractions[emailId]?.[opType]) {
        state.aiExtractions[emailId][opType] = state.aiExtractions[emailId][opType]!.filter(
          (_, i) => i !== index
        );
      }
    },

    addMessageToChat: (
      state,
      action: PayloadAction<{ emailId: string; message: UIMessage }>
    ) => {
      const { emailId, message } = action.payload;
      if (!state.aiChats[emailId]) {
        state.aiChats[emailId] = { messages: [], lastUpdated: Date.now() };
      }
      state.aiChats[emailId].messages.push(message);
      state.aiChats[emailId].lastUpdated = Date.now();
    },

    setAiChatHistory: (
      state,
      action: PayloadAction<{ emailId: string; messages: UIMessage[] }>
    ) => {
      const { emailId, messages } = action.payload;
      state.aiChats[emailId] = {
        messages,
        lastUpdated: Date.now(),
      };
    },

    clearAiData: (state) => {
      state.aiReviews = {};
      state.aiChats = {};
      state.aiExtractions = {};
    },
    // Merges partial draft updates into Redux state or deletes draft entry if all content fields are empty
    setEmailDraft: (
      state,
      action: PayloadAction<{ emailId: string; content?: string } & Partial<EmailDraft>>
    ) => {
      const { emailId, content, subject, body: bodyInput, ...rest } = action.payload;
      const existingDraft = state.drafts[emailId] || {};
      const updatedDraft: EmailDraft = { ...existingDraft, ...rest };

      const body = bodyInput !== undefined ? bodyInput : content;
      if (body !== undefined) {
        if (body.trim()) {
          updatedDraft.body = body;
        } else {
          delete updatedDraft.body;
        }
      }

      if (subject !== undefined) {
        if (subject === null || subject === "") {
          delete updatedDraft.subject;
        } else {
          updatedDraft.subject = subject;
        }
      }

      if ("replyTo" in action.payload && action.payload.replyTo === undefined) {
        delete updatedDraft.replyTo;
      }
      if ("replyCc" in action.payload && action.payload.replyCc === undefined) {
        delete updatedDraft.replyCc;
      }
      if ("replyBcc" in action.payload && action.payload.replyBcc === undefined) {
        delete updatedDraft.replyBcc;
      }

      if (!isDraftEmpty(updatedDraft)) {
        state.drafts[emailId] = updatedDraft;
      } else {
        delete state.drafts[emailId];
      }
    },

    // Removes a single draft from Redux state when sent or explicitly discarded
    clearEmailDraft: (state, action: PayloadAction<string>) => {
      delete state.drafts[action.payload];
    },

    // Reply-open state lives in Redux so composer visibility survives EmailDetails unmounting when switching tabs
    setReplyOpen: (
      state,
      action: PayloadAction<{ emailId: string; isOpen: boolean }>
    ) => {
      const { emailId, isOpen } = action.payload;
      if (isOpen) {
        state.openReplies[emailId] = true;
      } else {
        delete state.openReplies[emailId];
      }
    },

    // Clears reply-open state when composer is closed or sent, allowing EmailDetails remounts to respect closed state
    clearReplyOpen: (state, action: PayloadAction<string>) => {
      delete state.openReplies[action.payload];
    },

    // Purges all Redux email drafts on logout
    clearAllDrafts: (state) => {
      state.drafts = {};
    },

    setSortOrder: (state, action: PayloadAction<SortOrder>) => {
      state.sortOrder = action.payload;
    },

    setEmailActiveFilter: (state, action: PayloadAction<string | null>) => {
      state.activeFilter = action.payload;
    },

    setMyVoyagesEnabled: (state, action: PayloadAction<boolean>) => {
      state.myVoyagesEnabled = action.payload;
    },
    setRemovingVesselId: (state, action: PayloadAction<string | null>) => {
      state.removingVesselId = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchEmails.pending, (state) => {
        state.listLoading = true;
        state.error = null;
      })
      .addCase(
        fetchEmails.fulfilled,
        (state, action: PayloadAction<EmailListItem[]>) => {
          state.listLoading = false;
          state.emails = action.payload;
          state.unreadCount = action.payload.filter(
            (email) => !email.isRead
          ).length;
        }
      )
      .addCase(fetchEmails.rejected, (state, action) => {
        state.listLoading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchEmailById.pending, (state, action) => {
        const emailId = getEmailIdFromFetchArg(action.meta.arg);
        state.detailsLoading = true;
        state.detailsLoadingById[emailId] = true;
        if (state.selectedEmailId === emailId) {
          state.selectedEmailDetails = state.emailDetailsById[emailId] ?? null;
        }
        state.error = null;
      })
      .addCase(
        fetchEmailById.fulfilled,
        (state, action: PayloadAction<EmailDetails>) => {
          state.detailsLoading = false;
          const details = action.payload;
          if (details.attachments) {
            details.attachments = details.attachments.filter(
              (a) => !a.name?.toLowerCase().endsWith(".p7m")
            );
          }
          state.emailDetailsById[details.id] = details;
          state.detailsLoadingById[details.id] = false;
          if (state.selectedEmailId === details.id) {
            state.selectedEmailDetails = details;
          }
        }
      )
      .addCase(fetchEmailById.rejected, (state, action) => {
        const emailId = getEmailIdFromFetchArg(action.meta.arg);
        state.detailsLoading = false;
        state.detailsLoadingById[emailId] = false;
        state.error = action.payload as string;
      })
      .addCase(
        toggleEmailFlag.fulfilled,
        (state, action: PayloadAction<EmailDetails>) => {
          if (
            state.selectedEmailDetails &&
            state.selectedEmailDetails.id === action.payload.id
          ) {
            state.selectedEmailDetails = action.payload;
          }
          state.emailDetailsById[action.payload.id] = action.payload;
        }
      );
  },
});

export const {
  setViewMode,
  selectEmail,
  openEmailPreviewTab,
  openNewComposeTab,
  updateOpenEmailTabTitle,
  setActiveEmailTab,
  pinEmailTab,
  closeEmailTab,
  updateOpenEmailTabMashedEmail,
  changeFolder,
  changeMailbox,
  setAiReview,
  setAiExtractions,
  dismissAiExtractionItem,
  addMessageToChat,
  setAiChatHistory,
  clearAiData,
  setEmailDraft,
  clearEmailDraft,
  setReplyOpen,
  clearReplyOpen,
  clearAllDrafts,
  resetSelectedEmail,
  setSortOrder,
  setEmailActiveFilter,
  setMyVoyagesEnabled,
  setRemovingVesselId,
} = emailSlice.actions;
export default emailSlice.reducer;

// Selects the full drafts map from Redux state
export const selectEmailDrafts = (state: RootState) => state.emails.drafts;

// Selects an EmailDraft object by emailId from Redux state
export const selectEmailDraftByEmailId = (
  state: RootState,
  emailId: string
): EmailDraft | undefined => state.emails.drafts[emailId];

// Selects only the body text of a draft by emailId
export const selectEmailDraftBody = (
  state: RootState,
  emailId: string
): string => state.emails.drafts[emailId]?.body ?? "";

// Selects only the subject of a draft by emailId
export const selectEmailDraftSubject = (
  state: RootState,
  emailId: string
): string | undefined => state.emails.drafts[emailId]?.subject;

// Checks whether an email has active draft content for UI indicator chips
export const selectHasEmailDraft = (
  state: RootState,
  emailId: string
): boolean => {
  const draft = state.emails.drafts[emailId];
  return !isDraftEmpty(draft);
};
